/**
 * Translation service - loads all cached translations from the server, and provides a gt()
 * function similar to that formerly used on the PHP side.
 *
 * The former translation setup had extensive machinery to handle dynamically-changing content
 * sources - specifically, questionnaires and questions that could be added in the database.
 *
 * In this new simplified version, all translations are stored in CSV files (for ease of updating),
 * which are loaded at app startup.
 */
import Api from '@/providers/Api'
import * as _ from 'lodash'
import Papa from 'papaparse'
import {store} from '../apps/default/store'
import Theme from '@/providers/Theme'

export default {

    translationRows: [],

    async loadTranslations(language, store, User) {
        // NB: 'practitioner' file contains translations for items only shown to practitioners/admins
        // (more or less - don't count on this!)
        const csvs = await Promise.all([
            Api.loadTranslations('ui'),
            Api.loadTranslations('practitioner'),
            Api.loadTranslations('help'),
            Api.loadTranslations('help-practitioner'),
            Api.loadTranslations('questions')
        ])

        // NB: must load this last for override mechanism to work properly (see gt() below)
        const customFile = Theme.getThemeTranslationFilename()
        if (customFile) {
            csvs.push(await Api.loadTranslations(customFile))
        }

        _.each(csvs, (csv) => {
            Papa.parse(csv, {
                complete: (results, file) => {
                    if (results.errors.length) {
                        alert('Error loading translation file')
                    }
                    this.translationRows = _.concat(this.translationRows, results.data)
                }
            })
        })

        store.state.translationsLoaded = true
    },

    // TODO: eventually handle more than 2 languages
    switchLanguage(store) {
        if (store.state.language == 'en') {
            store.state.language = 'es'
        } else {
            store.state.language = 'en'
        }
        // NB: this setting is persisted in User.switchLanguage
    },

    /**
     * Returns the translation for a given key in the current language.
     *
     * NB: calling this from a component template will return a bare key
     * if the translations are not loaded yet - so the topmost component should include
     * v-if="$store.state.translationsLoaded" on the main template element!
     *
     * For phrases that require parameter substitution, this can
     * be done using regular string replacement - e.g.:
     *
     *      this.$i18n.gt('MESSAGE_REPORT_MISSING_DATA')
     *         .replace('{enter_them_here}', '...')
     *
     * (but the replacement phrase will sometimes also need translation).
     */
    gt(key) {
        // find the last row with this key - to allow for custom overrides of specific entries
        // (assumes custom translation file, if any, was loaded last)
        const row = _.findLast(this.translationRows, (row) => {
            return row[0] == key
        })
        if (row) {
            // return store.state.language == 'en' ? row[1] : '[[ ' + (row[2] ? row[2] : key) + ' ]]'
            return store.state.language == 'en' ? row[1] : (row[2] ? row[2] : key)
        } else {
            return null
        }
    },

    /**
     * Returns the translation of a question field
     * (as defined in QuestionnaireBase.php, QuestionnairePHQ9.php, etc.).
     *
     * Fields covered are:
     *     preamble, question_text, tip, minmax_message, validation_message,
     *     options (array), patient_advice, practitioner_advice
     *
     * Other fields, used only formerly in the Conifer questionnaire:
     *     popup_link, popup_title, popup_text
     */
    translateQuestionField(question, field) {
        return this.gt(`QUESTION_${question.name}_${field}`)
    },

    /**
     * Returns the translation of a "plan goal" template, e.g. "Goal for LDL: __ mg/dL"
     */
    getPlanGoalTemplate(scoreName) {
        return this.gt(`QUESTION_${scoreName}_PLAN_GOAL`)
    },

    /**
     * Returns the translation of a "note summary" template, which may or may not include a slot
     * (e.g. "Movement Score" or "Total Cholesterol: __ mg/dL")
     */
    getNoteSummaryTemplate(scoreName) {
        return this.gt(`QUESTION_${scoreName}_NOTE_SUMMARY`)
    },

    /**
     * Returns the translation of a "note summary level" (e.g. "Fair" for Movement Score)
     * based on a score threshold.
     */
    getNoteSummaryThresholdDescription(scoreName, thresholdIndex) {
        return this.gt(`QUESTION_${scoreName}_NOTE_SUMMARY_LEVELS_${thresholdIndex}`)
    },

    getPatientAdvice(questionName) {
        return this.gt(`QUESTION_${questionName}_PATIENT_ADVICE`)
    },

    getPractitionerAdvice(questionName) {
        return this.gt(`QUESTION_${questionName}_PRACTITIONER_ADVICE`)
    },

    getNoteTypeLabel(noteType) {
        return this.gt(`LABEL_SURVEY_NOTE_TYPE_${noteType}`)
    }
}
